/**
 * Generalized, manifest-driven entity detail model. A per-layer enricher (keyed
 * by layer id) builds grouped, labelled sections + an optional image; layers
 * with no enricher fall back to their declared manifest displayFields. One model
 * powers the detail modal for satellites, aircraft, vessels, and everything else.
 */

import type { Track } from '@vtak/shared';
import type { MarkerData } from '@/map/markers/marker-state';
import { SATELLITE_LAYER } from '@/map/coverage/coverage-frame';
import { enrichSatellite } from './satellite-enricher';
import { enrichAircraft, AIRCRAFT_LAYER } from './aircraft-enricher';
import { enrichVessel, VESSEL_LAYER } from './vessel-enricher';

/** A labelled detail value. */
export interface DetailRow {
  label: string;
  value: string;
}

/** A titled group of detail rows. */
export interface DetailSection {
  title: string;
  rows: DetailRow[];
}

/** The resolved detail view for one entity. */
export interface EntityDetail {
  /** Real photo URL (e.g. aircraft), when available. */
  imageUrl?: string;
  /** Grouped detail sections. */
  sections: DetailSection[];
  /** Image/source attribution line, when required by the source. */
  attribution?: string;
  /** Notes about richer data that needs an API key (shown as follow-up chips). */
  followUps?: string[];
}

/** Builds an {@link EntityDetail} from a marker + its source track. */
export type EntityEnricher = (data: MarkerData, track: Track | undefined) => EntityDetail;

/** Per-layer enrichers; layers absent here use the manifest-field fallback. */
const ENRICHERS: ReadonlyMap<string, EntityEnricher> = new Map([
  [SATELLITE_LAYER, enrichSatellite],
  [AIRCRAFT_LAYER, enrichAircraft],
  [VESSEL_LAYER, enrichVessel],
]);

/** Resolve the detail view for a selected entity. */
export function resolveEntityDetail(data: MarkerData, track: Track | undefined): EntityDetail {
  const enricher = ENRICHERS.get(data.layer);
  return enricher ? enricher(data, track) : fallbackDetail(data);
}

/** Fallback: render the manifest displayFields (plus type/position) as one section. */
function fallbackDetail(data: MarkerData): EntityDetail {
  const rows: DetailRow[] = [
    { label: 'Type', value: data.cotType },
    { label: 'Position', value: formatCoords(data.position) },
    ...(data.details ?? []).map((detail) => ({ label: detail.label, value: detail.value })),
  ];
  return { imageUrl: data.imageUrl, sections: [{ title: 'Details', rows }] };
}

/** Format a coordinate pair to 4 decimals. */
export function formatCoords(pos: google.maps.LatLngLiteral): string {
  return `${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)}`;
}

/** A string detail row, blank when the value is absent (the modal hides blanks). */
export function textRow(label: string, value: unknown): DetailRow {
  return { label, value: value === undefined || value === null ? '' : String(value) };
}
